<template>
  <div class="user-insights" :class="{ 'user-insights--dark': isDarkSkin }">

    <div class="ui-toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <!-- One-click department shortcuts: fills the business-unit filter with every unit of that department -->
      <div class="dept-quick" v-if="availableDepartments.length">
        <span class="dept-quick__label">{{ $t('filters.departments') }}</span>
        <button v-for="dept in availableDepartments" :key="dept" type="button"
                class="dept-quick__pill" :class="['dept-quick__pill--' + dept.toLowerCase(), { 'is-active': isDeptActive(dept) }]"
                @click="toggleDept(dept)">
          <i :class="isDeptActive(dept) ? 'fa-solid fa-check-circle' : 'fa-regular fa-circle'"></i>
          {{ $t(`businessUnitDepartments.${dept}`) }}
          <span class="dept-quick__count">{{ deptBuIds(dept).length }}</span>
        </button>
      </div>
      <calendar-bar v-model="dateRange" @input="onRangeChanged"/>
    </div>

    <!-- ============ CONTROLS (search + export) ============ -->
    <div class="ui-controls">
      <div class="ui-search">
        <feather-icon icon="SearchIcon" size="16" class="ui-search__icon"/>
        <input
            type="text"
            class="ui-search__input"
            v-model="searchText"
            :placeholder="$t('userInsights.searchUser')"
            @input="onSearchInput"
        />
        <button v-if="searchText" type="button" class="ui-search__clear" @click="clearSearch">
          <feather-icon icon="XIcon" size="14"/>
        </button>
      </div>

      <!-- Agent-code filter (User.code) — same lens as the Sales Dashboard,
           applied on the server so it works across all pages and the export -->
      <div class="ui-search ui-search--code">
        <i class="fa-solid fa-id-badge ui-search__icon"></i>
        <input
            type="text"
            class="ui-search__input"
            v-model="agentCode"
            :placeholder="$t('salesDashboard.agentCodeFilter')"
            @input="onSearchInput"
        />
        <button v-if="agentCode" type="button" class="ui-search__clear" @click="clearAgentCode">
          <feather-icon icon="XIcon" size="14"/>
        </button>
      </div>

      <button type="button" class="ui-export" :disabled="exporting" @click="exportExcel">
        <b-spinner v-if="exporting" small class="mr-50"/>
        <i v-else class="fas fa-file-excel mr-50"></i>
        <span>{{ $t('userInsights.export') }}</span>
      </button>

      <button type="button" class="ui-eye" :class="{ 'is-on': hideAmounts }"
              v-b-tooltip :title="hideAmounts ? $t('userInsights.showAmounts') : $t('userInsights.hideAmounts')"
              @click="toggleAmounts">
        <i :class="hideAmounts ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
      </button>
    </div>

    <!-- ============ KPI CARDS — same cards as the Command Center, for this filter ============ -->
    <insights-kpi-cards :kpis="kpis" :loading="kpisLoading" :hide-amounts="hideAmounts"
                        :scope="{ range: dateRange, orgIds: filter.orgIds, buIds: filter.buIds }"/>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <!-- ============ RANKED LIST ============ -->
      <b-card no-body class="ui-list" v-if="listRows.length">
        <b-card-header class="pb-50">
          <h5 class="mb-0"><i class="fas fa-ranking-star mr-50"></i>{{ $t('userInsights.leaderboard') }}</h5>
        </b-card-header>
        <b-card-body class="pt-75">
          <div class="ui-tablewrap">
            <div v-for="item in listRows" :key="item.userId" class="ui-lb-row" @click="openInsight(item)">
              <div class="ui-lb-row__rank">#{{ item.rank }}</div>
              <div class="ui-lb-row__id">
                <div class="ui-lb-row__avatar" :style="{ background: avatarColor(item.name) }">{{ initials(item.name) }}</div>
                <div class="ui-lb-row__meta">
                  <div class="ui-lb-row__name">
                    {{ item.name }}
                    <span v-for="t in (item.buTypes || [])" :key="t"
                          class="ui-sr" :class="t === 'SALES' ? 'ui-sr--s' : 'ui-sr--r'"
                          v-b-tooltip :title="t === 'SALES' ? $t('userInsights.sales') : $t('userInsights.retention')">
                      {{ t === 'SALES' ? 'S' : 'R' }}
                    </span>
                  </div>
                  <div class="ui-lb-row__sub">
                    <span v-if="item.username"><i class="fas fa-at mr-25"></i>{{ item.username }}</span>
                    <span v-if="orgName(item.orgId)" :class="{ 'ml-75': item.username }">
                      <i class="fas fa-building mr-25"></i>{{ orgName(item.orgId) }}
                    </span>
                  </div>
                  <div class="ui-lb-row__track"><div class="ui-lb-row__fill" :style="{ width: barWidth(item) }"></div></div>
                </div>
              </div>
              <div class="ui-blk ui-blk--ftd">
                <span class="ui-blk__label">{{ $t('userInsights.ftds') }} <span class="ui-blk__count" :class="{ 'ui-masked': hideAmounts }">({{ item.ftdCount }})</span></span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.ftdAmount) }}</span>
              </div>
              <div class="ui-blk ui-blk--ret">
                <span class="ui-blk__label">{{ $t('userInsights.retention') }}</span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.retentionAmount) }}</span>
              </div>
              <div class="ui-blk ui-blk--wd">
                <span class="ui-blk__label">{{ $t('userInsights.withdrawals') }}</span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.withdrawals) }}</span>
              </div>
              <div class="ui-blk ui-blk--net">
                <span class="ui-blk__label">{{ $t('userInsights.net') }}</span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.net) }}</span>
              </div>
              <div class="ui-blk ui-blk--bonus">
                <span class="ui-blk__label">{{ $t('userInsights.bonus') }}</span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.bonus) }}</span>
              </div>
              <div class="ui-blk ui-blk--fake" v-b-tooltip :title="$t('userInsights.fakeHint')">
                <span class="ui-blk__label">{{ $t('userInsights.fake') }} <span class="ui-blk__count" :class="{ 'ui-masked': hideAmounts }">({{ item.fakeCount || 0 }})</span></span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.fake) }}</span>
              </div>
              <div class="ui-blk ui-blk--mini">
                <span class="ui-blk__pair"><b>{{ item.clientCount }}</b> {{ $t('userInsights.clients') }}</span>
                <span class="ui-blk__pair"><b>{{ item.callCount }}</b> {{ $t('userInsights.calls') }}</span>
              </div>
              <div class="ui-blk ui-blk--total">
                <span class="ui-blk__label">{{ $t('userInsights.totalBrought') }}</span>
                <span class="ui-blk__value" :class="{ 'ui-masked': hideAmounts }">{{ money(item.deposits) }}</span>
              </div>
              <div class="ui-lb-row__go"><feather-icon icon="ChevronRightIcon" size="16"/></div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- ============ PAGINATION ============ -->
      <div class="ui-pager" v-if="!loading && items.length">
        <b-pagination
            v-if="totalRows > perPage"
            v-model="page"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            class="mb-0"
            @change="onPageChange"
        />
        <b-select style="width: 75px" size="sm" v-model="perPage" @change="onSizeChange">
          <b-select-option :value="20">20</b-select-option>
          <b-select-option :value="50">50</b-select-option>
          <b-select-option :value="100">100</b-select-option>
        </b-select>
        <span class="ui-pager__info">{{ items.length }} {{ $t('userInsights.showingOf') }} {{ totalRows }}</span>
      </div>

      <!-- ============ EMPTY ============ -->
      <div v-if="!loading && !items.length" class="ui-empty">
        <i class="fas fa-medal ui-empty__icon"></i>
        <div class="ui-empty__title">{{ $t('userInsights.noData') }}</div>
        <div class="ui-empty__hint">{{ $t('userInsights.noDataHint') }}</div>
      </div>

    </b-overlay>

    <user-insight-modal ref="insightModal"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CalendarBar from '@/components/widget/CalendarBar';
import FilterCard from '@/components/widget/FilterCard';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import UserInsightsFilterFields from '@/model/filter/UserInsightsFilterFields';
import UserInsightModal from '@/components/modal/UserInsightModal';
import InsightsKpiCards from '@/components/partial/insights/InsightsKpiCards';

const REFRESH_DEBOUNCE_MS = 250;
const SEARCH_DEBOUNCE_MS = 500;
const PAGE_SIZE = 20;

export default {
  name: 'UserInsights',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  components: { CalendarBar, FilterCard, UserInsightModal, InsightsKpiCards },
  data: () => ({
    filter: { orgIds: [], buIds: [], userIds: [] },
    filterFields: [],
    dateRange: { from: null, until: null },
    searchText: '',
    agentCode: '',
    page: 1,
    perPage: PAGE_SIZE,
    pagination: { page: 1, pages: 0, total: 0 },
    items: [],
    loading: false,
    // Command Center KPI cards for the same filter (separate request)
    kpis: null,
    kpisLoading: false,
    kpisSeq: 0, // stale-response guard
    kpisKey: null, // filter the cards were last loaded for (paging never reloads them)
    exporting: false,
    // Privacy eye: default OPEN — click blurs the figures on demand.
    hideAmounts: false,
    refreshTimer: null,
    searchTimer: null,
  }),
  watch: {
    filter: { handler: 'onFilterChanged', deep: true },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['getOrganizationNameById', 'allBusinessUnits']),
    viewKey() { return this.$route.name || 'user-insights'; },
    // Departments that actually have business units (SALES / RETENTION / LANDING).
    availableDepartments() {
      const present = new Set((this.allBusinessUnits || []).map(b => b.type).filter(Boolean));
      return ['SALES', 'RETENTION', 'LANDING'].filter(t => present.has(t));
    },
    totalRows() { return (this.pagination && this.pagination.total) || this.items.length; },
    listRows() { return this.items; },
    maxDeposit() { return Math.max(...this.items.map(i => i.deposits || 0), 0); },
  },
  created() {
    this.filterFields = UserInsightsFilterFields();
    this.dateRange = {
      from: this.$moment().startOf('month').format('YYYY-MM-DD'),
      until: this.$moment().endOf('month').format('YYYY-MM-DD'),
    };
  },
  mounted() { this.$root.$on('dashboard::refresh', this.scheduleRefresh); },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
    if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
  },
  methods: {
    ...mapActions('dashboard', ['getUserInsightsTop', 'downloadInsightsExcel', 'getUserInsightsKpis']),

    // ---- Department quick-pick: one click selects ALL units of a department ----
    deptBuIds(dept) {
      let bus = (this.allBusinessUnits || []).filter(b => b.type === dept);
      const orgs = this.filter.orgIds || [];
      if (orgs.length) {
        bus = bus.filter(b => orgs.includes(b.orgId));
      }
      return bus.map(b => b.id);
    },
    isDeptActive(dept) {
      const ids = this.deptBuIds(dept);
      const sel = this.filter.buIds || [];
      return ids.length > 0 && ids.every(id => sel.includes(id));
    },
    toggleDept(dept) {
      const ids = this.deptBuIds(dept);
      if (!ids.length) return;
      const sel = new Set(this.filter.buIds || []);
      if (this.isDeptActive(dept)) {
        ids.forEach(id => sel.delete(id));
      } else {
        ids.forEach(id => sel.add(id));
      }
      this.filter.buIds = [...sel]; // deep watcher -> refresh; BU select shows them
    },

    money(v) { return this.convertToEURO(v || 0); },
    toggleAmounts() {
      this.hideAmounts = !this.hideAmounts;
    },
    orgName(orgId) { return orgId ? this.getOrganizationNameById(orgId) : null; },
    barWidth(item) {
      if (!this.maxDeposit) return '0%';
      return Math.max(3, Math.round(((item.deposits || 0) / this.maxDeposit) * 100)) + '%';
    },

    buildPayload() {
      const search = this.searchText && this.searchText.trim() ? this.searchText.trim() : null;
      const userCode = this.agentCode && this.agentCode.trim() ? this.agentCode.trim() : null;
      return {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
        search,
        userCode,
        page: this.page,
        size: this.perPage,
      };
    },

    onFilterChanged() { this.page = 1; this.scheduleRefresh(); },
    onRangeChanged() { this.page = 1; this.scheduleRefresh(); },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.scheduleRefresh(); }, SEARCH_DEBOUNCE_MS);
    },
    clearSearch() {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
      this.searchText = '';
      this.page = 1;
      this.scheduleRefresh();
    },
    clearAgentCode() {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
      this.agentCode = '';
      this.page = 1;
      this.scheduleRefresh();
    },
    onPageChange(page) {
      this.page = page;
      this.scheduleRefresh();
    },
    // Page-size select: restart from page 1 with the new size
    onSizeChange() {
      this.page = 1;
      this.scheduleRefresh();
    },

    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      this.refreshKpis();
      this.loading = true;
      const payload = this.buildPayload();
      this.getUserInsightsTop(payload)
          .then(res => {
            const r = res || {};
            this.items = r.data || [];
            this.pagination = r.pagination || { page: this.page, pages: 0, total: this.items.length };
          })
          .catch(err => this.showServerErrorNotification('fetchDataError', 'userInsights', err))
          .finally(() => { this.loading = false; });
    },

    // KPI cards: page-independent (same filter, no paging); only the newest answer lands
    refreshKpis() {
      const payload = this.buildPayload();
      delete payload.page;
      delete payload.size;
      const key = JSON.stringify(payload);
      if (key === this.kpisKey && this.kpis) return;
      this.kpisKey = key;
      const seq = ++this.kpisSeq;
      this.kpisLoading = true;
      this.getUserInsightsKpis(payload)
          .then(kpis => { if (seq === this.kpisSeq) this.kpis = kpis; })
          .catch(err => {
            if (seq !== this.kpisSeq) return;
            this.kpisKey = null; // retry on the next refresh
            this.showServerErrorNotification('fetchDataError', 'userInsights', err);
          })
          .finally(() => { if (seq === this.kpisSeq) this.kpisLoading = false; });
    },

    exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      const filter = this.buildPayload();
      delete filter.page;
      delete filter.size;
      this.downloadInsightsExcel({ kind: 'users', filter })
          .catch(err => this.showServerErrorNotification('downloadFileError', 'userInsights', err))
          .finally(() => { this.exporting = false; });
    },

    openInsight(item) {
      // Pass the page's date range so the modal stats/drills match the filter
      this.$refs.insightModal.show(item.userId, item.rank,
          { from: this.dateRange.from, until: this.dateRange.until });
    },
    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    avatarColor(name) {
      const palette = ['#007BFF', '#28c76f', '#00cfe8', '#ff9f43', '#ea5455', '#6e6b7b', '#ff6f91', '#3fbff7'];
      if (!name) return palette[0];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
      }
      return palette[hash % palette.length];
    },
  },
};
</script>

<style scoped lang="scss">
.user-insights {
  .ui-toolbar { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
}

/* ===== Controls (search + export) ===== */
.ui-controls {
  display: flex; align-items: center; gap: 0.75rem;
  margin-bottom: 1rem; flex-wrap: wrap;
}
.ui-search {
  position: relative; flex: 1; min-width: 220px; max-width: 380px;

  &__icon {
    position: absolute; left: 0.8rem; top: 50%; transform: translateY(-50%);
    color: #9a9dad; pointer-events: none;
  }
  &__input {
    width: 100%; height: 38px; border-radius: 999px;
    border: 1px solid rgba(110, 117, 159, 0.24);
    background: #fff; color: #5e5873;
    padding: 0 2.2rem 0 2.4rem; font-size: 0.9rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus { outline: none; border-color: #7367f0; box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12); }
    &::placeholder { color: #b4b7bd; }
  }
  &__clear {
    position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%);
    width: 24px; height: 24px; border-radius: 50%; border: none; cursor: pointer; padding: 0;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(110, 117, 159, 0.12); color: #6e6b7b;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: rgba(234, 84, 85, 0.15); color: #ea5455; }
  }
}
.ui-search--code {
  flex: 0 1 190px; min-width: 150px;

  .ui-search__icon { color: #7367f0; font-size: 0.85rem; }
}
.ui-export {
  display: inline-flex; align-items: center; margin-left: auto;
  border: none; border-radius: 999px; cursor: pointer;
  padding: 0 1.25rem; height: 38px;
  color: #fff; font-weight: 700; font-size: 0.85rem;
  background: linear-gradient(135deg, #34d399, #059669);
  box-shadow: 0 8px 18px -10px rgba(5, 150, 105, 0.7);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 24px -10px rgba(5, 150, 105, 0.8);
    filter: brightness(1.04);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
}

/* Privacy eye — banking-app style toggle that blurs every money/FTD figure */
.ui-eye {
  display: inline-flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; flex-shrink: 0;
  border: 1px solid rgba(110, 117, 159, 0.24); border-radius: 999px;
  background: #fff; color: #5e5873; cursor: pointer; font-size: 0.95rem;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover { border-color: #7367f0; color: #7367f0; }
  &.is-on {
    background: #7367f0; border-color: #7367f0; color: #fff;
    box-shadow: 0 8px 18px -10px rgba(115, 103, 240, 0.8);
  }
}

/* Applied to every masked figure while the eye is on */
.ui-masked {
  filter: blur(7px);
  user-select: none;
  pointer-events: none;
}

/* ===== Ranked list: elevated card rows — same design language as the podium
   and KPI tiles (soft shadows, tinted stat blocks, gradient total) ===== */
.ui-list { border-radius: 16px; background: transparent; box-shadow: none; border: none; }
.ui-tablewrap { overflow-x: auto; padding: 2px; }

.ui-lb-row {
  display: grid;
  grid-template-columns: 48px minmax(230px, 1fr) 150px 130px 130px 130px 130px 130px 105px 185px 30px;
  gap: 0 0.7rem;
  align-items: center;
  min-width: 1540px;
  background: #fff;
  border: 1px solid rgba(110, 117, 159, 0.1);
  border-radius: 14px;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.6rem;
  cursor: pointer;
  box-shadow: 0 6px 16px -12px rgba(20, 24, 50, 0.18);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(115, 103, 240, 0.35);
    box-shadow: 0 14px 30px -14px rgba(115, 103, 240, 0.4);

    .ui-lb-row__go { background: #7367f0; color: #fff; }
  }

  &__rank {
    width: 40px; height: 40px; border-radius: 12px;
    display: inline-flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.16), rgba(115, 103, 240, 0.06));
    color: #7367f0; font-weight: 800; font-size: 0.82rem;
  }
  &__id { display: flex; align-items: center; gap: 0.7rem; min-width: 0; }
  &__avatar {
    width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    color: #fff; font-weight: 800; font-size: 0.85rem;
    box-shadow: 0 4px 10px -4px rgba(20, 24, 50, 0.4);
  }
  &__meta { min-width: 0; flex: 1; }
  &__name { font-weight: 700; color: #5e5873; font-size: 0.95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__sub { font-size: 0.72rem; color: #9a9dad; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__track {
    margin-top: 5px; height: 5px; width: 100%;
    border-radius: 999px; background: rgba(110, 117, 159, 0.14); overflow: hidden;
  }
  &__fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #7367f0, #9e95f5);
    transition: width 0.5s ease;
  }
  &__go {
    width: 26px; height: 26px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(110, 117, 159, 0.1); color: #9a9dad;
    transition: background 0.18s ease, color 0.18s ease;
    justify-self: end;
  }
}

/* Mini KPI blocks inside each row — label on top, bold value below */
.ui-blk {
  display: flex; flex-direction: column; align-items: flex-end;
  border-radius: 10px; padding: 0.4rem 0.6rem; line-height: 1.25;
  background: rgba(110, 117, 159, 0.07);

  &__label {
    font-size: 0.56rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.07em; color: #9a9dad; white-space: nowrap;
  }
  &__value {
    font-size: 0.95rem; font-weight: 800; color: #6e6b7b;
    font-variant-numeric: tabular-nums; white-space: nowrap;
  }
  &__count {
    font-size: 0.62rem; font-weight: 800; letter-spacing: 0;
    color: #6e6b7b; opacity: 0.75;
  }
  &__pair {
    font-size: 0.74rem; font-weight: 600; color: #9a9dad; white-space: nowrap;
    font-variant-numeric: tabular-nums;
    b { font-weight: 800; color: #6e6b7b; }
  }

  &--ftd { background: rgba(246, 168, 33, 0.12);
    .ui-blk__label { color: rgba(217, 140, 0, 0.85); }
    .ui-blk__value { color: #d98c00; }
    .ui-blk__count { color: #b97e10; }
  }
  &--ret { background: rgba(77, 163, 255, 0.11);
    .ui-blk__label { color: rgba(47, 127, 214, 0.85); }
    .ui-blk__value { color: #2f7fd6; }
  }
  &--wd { background: rgba(234, 84, 85, 0.09);
    .ui-blk__label { color: rgba(234, 84, 85, 0.75); }
    .ui-blk__value { color: #ea5455; }
  }
  &--net { background: rgba(40, 199, 111, 0.1);
    .ui-blk__label { color: rgba(33, 160, 89, 0.85); }
    .ui-blk__value { color: #28c76f; }
  }
  &--bonus { background: rgba(232, 62, 140, 0.09);
    .ui-blk__label { color: rgba(232, 62, 140, 0.75); }
    .ui-blk__value { color: #e83e8c; }
  }
  &--fake { background: rgba(255, 159, 67, 0.13);
    .ui-blk__label { color: rgba(232, 119, 34, 0.85); }
    .ui-blk__value { color: #e87722; }
    .ui-blk__count { color: #c9651a; }
  }
  &--mini {
    gap: 1px;
    .ui-blk__value { font-size: 0.85rem; font-weight: 700; color: #9a9dad; }
  }
  &--total {
    background: linear-gradient(135deg, #7367f0, #5e50ee);
    box-shadow: 0 6px 14px -6px rgba(115, 103, 240, 0.55);
    .ui-blk__label { color: rgba(255, 255, 255, 0.8); }
    .ui-blk__value { color: #fff; font-size: 1.08rem; }
  }
}

/* ===== Pagination ===== */
.ui-pager {
  display: flex; align-items: center; gap: 0.75rem; margin-top: 1rem;
  &__info { font-size: 0.78rem; color: #9a9dad; }
}

/* ===== Empty state ===== */
.ui-empty {
  text-align: center; padding: 3.5rem 1rem; color: #9a9dad;
  &__icon { font-size: 2.4rem; opacity: 0.5; }
  &__title { font-weight: 700; margin-top: 0.8rem; font-size: 1.05rem; }
  &__hint { font-size: 0.82rem; margin-top: 0.2rem; }
}

/* ===== Dark mode ===== */
.user-insights--dark {
  .ui-search__input {
    background: #283046; border-color: rgba(255, 255, 255, 0.12); color: #d0d2d6;
    &::placeholder { color: #676d7d; }
  }
  .ui-search__clear { background: rgba(255, 255, 255, 0.08); color: #b4b7bd; }
  .ui-eye {
    background: #283046; border-color: rgba(255, 255, 255, 0.12); color: #b4b7bd;
    &:hover { border-color: #9e95f5; color: #9e95f5; }
    &.is-on { background: #7367f0; border-color: #7367f0; color: #fff; }
  }
  .ui-lb-row {
    background: #283046;
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 8px 20px -14px rgba(0, 0, 0, 0.6);
    &:hover { border-color: rgba(158, 149, 245, 0.4); }
    &__name { color: #d0d2d6; }
    &__track { background: rgba(255, 255, 255, 0.1); }
    &__go { background: rgba(255, 255, 255, 0.08); }
  }
  .ui-blk {
    background: rgba(255, 255, 255, 0.05);
    .ui-blk__value { color: #d0d2d6; }
    .ui-blk__count { color: #fff; opacity: 0.85; }
    .ui-blk__pair { b { color: #d0d2d6; } }
    &--ftd { background: rgba(246, 168, 33, 0.14); .ui-blk__value { color: #f6b64b; } .ui-blk__count { color: #fff; } }
    &--ret { background: rgba(77, 163, 255, 0.14); .ui-blk__value { color: #6fb2ff; } }
    &--wd { background: rgba(234, 84, 85, 0.14); .ui-blk__value { color: #ff8a8b; } }
    &--net { background: rgba(40, 199, 111, 0.14); .ui-blk__value { color: #48da8c; } }
    &--bonus { background: rgba(232, 62, 140, 0.16); .ui-blk__value { color: #f77eb9; } }
    &--fake { background: rgba(255, 159, 67, 0.16); .ui-blk__value { color: #ffb054; } .ui-blk__count { color: #fff; } }
    &--mini .ui-blk__value { color: #9a9dad; }
    &--total { background: linear-gradient(135deg, #7367f0, #5e50ee); .ui-blk__value { color: #fff; } }
  }
}

@media (max-width: 992px) {
  /* Narrow screens: the grid keeps its shape and scrolls horizontally */
  .ui-search { max-width: none; }
}

/* ===== S / R department badges next to agent names ===== */
.ui-sr {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  font-size: 0.62rem;
  font-weight: 800;
  margin-left: 0.3rem;
  vertical-align: middle;
  color: #fff;

  &--s { background: linear-gradient(135deg, #34d399, #059669); }
  &--r { background: linear-gradient(135deg, #4da3ff, #0062cc); }
}

/* ===== Department quick pills (same look as the sales dashboard) ===== */
.dept-quick {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.6rem 0.85rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;

  &__label {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    transition: all 0.18s ease;

    &--sales:hover { background: rgba(16, 185, 129, 0.1); color: #10b981; }
    &--retention:hover { background: rgba(0, 123, 255, 0.1); color: #007BFF; }
    &--landing:hover { background: rgba(245, 158, 11, 0.12); color: #d97706; }

    &--sales.is-active {
      color: #fff;
      background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%);
      box-shadow: 0 6px 14px -6px rgba(16, 185, 129, 0.55);
    }
    &--retention.is-active {
      color: #fff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);
    }
    &--landing.is-active {
      color: #fff;
      background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 60%, #d97706 100%);
      box-shadow: 0 6px 14px -6px rgba(245, 158, 11, 0.55);
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    font-size: 0.68rem;
    font-weight: 700;
  }
  &__pill.is-active .dept-quick__count { background: rgba(0, 0, 0, 0.16); }
}

.user-insights--dark .dept-quick {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  .dept-quick__label { border-right-color: rgba(255, 255, 255, 0.08); }
  .dept-quick__pill { color: #b4b7bd; }
  .dept-quick__count { background: rgba(255, 255, 255, 0.1); }
}
</style>
